import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const services = [
  { name: 'SEO & Organic Growth', slug: 'seo-organic-growth' },
  { name: 'Performance Marketing', slug: 'performance-marketing' },
  { name: 'Website Design & Development', slug: 'website-design-development' },
  { name: 'Social Media Marketing', slug: 'social-media-marketing' },
  { name: 'Conversion Rate Optimization', slug: 'conversion-rate-optimization' },
  { name: 'Branding & Strategy', slug: 'branding-strategy' },
];

const industries = [
  { name: 'Healthcare & Aesthetics', slug: 'healthcare-aesthetics' },
  { name: 'Education & Coaching', slug: 'education-coaching' },
  { name: 'Real Estate', slug: 'real-estate' },
  { name: 'Professional Services', slug: 'professional-services' },
  { name: 'Local Businesses', slug: 'local-businesses' },
  { name: 'Startups & SMEs', slug: 'startups-smes' },
];

export const Header = ({ onOpenAuditModal }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white/80 backdrop-blur-sm'
        }`}
      >
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img 
                src="/images/logo-icon.png" 
                alt="Digital Startup" 
                className="h-10 md:h-11 w-auto"
              />
              <span className="text-xl font-bold text-[#1A1A1A] hidden sm:block">
                Digital <span className="text-[#FFAA17]">Startup</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
              <Link
                to="/"
                className={`px-4 py-2 rounded-full transition-colors ${
                  isActive('/') ? 'text-[#FFAA17] font-semibold' : 'text-[#525252] hover:text-[#FFAA17]'
                }`}
                data-testid="nav-home"
              >
                Home
              </Link>

              {/* What We Do Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 px-4 py-2 rounded-full text-[#525252] hover:text-[#FFAA17] transition-colors focus:outline-none">
                  What We Do <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-64 bg-white border border-gray-100 shadow-xl rounded-xl p-2">
                  {services.map((service) => (
                    <DropdownMenuItem key={service.slug} asChild>
                      <Link
                        to={`/services/${service.slug}`}
                        className="w-full px-4 py-2.5 rounded-lg hover:bg-[#FFAA17]/5 hover:text-[#FFAA17] transition-colors"
                        data-testid={`nav-service-${service.slug}`}
                      >
                        {service.name}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Industries Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 px-4 py-2 rounded-full text-[#525252] hover:text-[#FFAA17] transition-colors focus:outline-none">
                  Industries <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-64 bg-white border border-gray-100 shadow-xl rounded-xl p-2">
                  {industries.map((industry) => (
                    <DropdownMenuItem key={industry.slug} asChild>
                      <Link
                        to={`/industries/${industry.slug}`}
                        className="w-full px-4 py-2.5 rounded-lg hover:bg-[#FFAA17]/5 hover:text-[#FFAA17] transition-colors"
                        data-testid={`nav-industry-${industry.slug}`}
                      >
                        {industry.name}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Link
                to="/work"
                className={`px-4 py-2 rounded-full transition-colors ${
                  isActive('/work') ? 'text-[#FFAA17]' : 'text-[#525252] hover:text-[#FFAA17]'
                }`}
                data-testid="nav-work"
              >
                Work
              </Link>

              <Link
                to="/insights"
                className={`px-4 py-2 rounded-full transition-colors ${
                  isActive('/insights') ? 'text-[#FFAA17]' : 'text-[#525252] hover:text-[#FFAA17]'
                }`}
                data-testid="nav-insights"
              >
                Insights
              </Link>

              <Link
                to="/about"
                className={`px-4 py-2 rounded-full transition-colors ${
                  isActive('/about') ? 'text-[#FFAA17]' : 'text-[#525252] hover:text-[#FFAA17]'
                }`}
                data-testid="nav-about"
              >
                About
              </Link>

              <Link
                to="/contact"
                className={`px-4 py-2 rounded-full transition-colors ${
                  isActive('/contact') ? 'text-[#FFAA17]' : 'text-[#525252] hover:text-[#FFAA17]'
                }`}
                data-testid="nav-contact"
              >
                Contact
              </Link>
            </nav>

            {/* CTA Button */}
            <div className="flex items-center gap-4">
              <Button
                onClick={onOpenAuditModal}
                className="hidden md:flex bg-[#FFAA17] hover:bg-[#E69500] text-[#1A1A1A] font-semibold rounded-full px-6 shadow-lg shadow-amber-500/20 hover:shadow-amber-500/30 transition-all duration-300"
                data-testid="header-cta-button"
              >
                Get Free Growth Audit
              </Button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-[#1A1A1A]"
                data-testid="mobile-menu-toggle"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 bg-white z-40 transform transition-transform duration-300 lg:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="mobile-menu"
      >
        <div className="pt-20 px-6 pb-6 h-full overflow-y-auto">
          <nav className="flex flex-col gap-4">
            <Link
              to="/"
              className="text-lg font-medium text-[#1A1A1A] py-2 border-b border-gray-100"
              data-testid="mobile-nav-home"
            >
              Home
            </Link>

            <div className="py-2 border-b border-gray-100">
              <p className="text-sm font-semibold text-[#FFAA17] mb-2">What We Do</p>
              {services.map((service) => (
                <Link
                  key={service.slug}
                  to={`/services/${service.slug}`}
                  className="block py-2 text-[#525252] hover:text-[#FFAA17]"
                  data-testid={`mobile-nav-service-${service.slug}`}
                >
                  {service.name}
                </Link>
              ))}
            </div>

            <div className="py-2 border-b border-gray-100">
              <p className="text-sm font-semibold text-[#FFAA17] mb-2">Industries</p>
              {industries.map((industry) => (
                <Link
                  key={industry.slug}
                  to={`/industries/${industry.slug}`}
                  className="block py-2 text-[#525252] hover:text-[#FFAA17]"
                  data-testid={`mobile-nav-industry-${industry.slug}`}
                >
                  {industry.name}
                </Link>
              ))}
            </div>

            <Link
              to="/work"
              className="text-lg font-medium text-[#1A1A1A] py-2 border-b border-gray-100"
              data-testid="mobile-nav-work"
            >
              Work
            </Link>

            <Link
              to="/insights"
              className="text-lg font-medium text-[#1A1A1A] py-2 border-b border-gray-100"
              data-testid="mobile-nav-insights"
            >
              Insights
            </Link>

            <Link
              to="/about"
              className="text-lg font-medium text-[#1A1A1A] py-2 border-b border-gray-100"
              data-testid="mobile-nav-about"
            >
              About
            </Link>

            <Link
              to="/contact"
              className="text-lg font-medium text-[#1A1A1A] py-2 border-b border-gray-100"
              data-testid="mobile-nav-contact"
            >
              Contact
            </Link>

            <Button
              onClick={() => {
                setIsMobileMenuOpen(false);
                onOpenAuditModal();
              }}
              className="mt-4 bg-[#FFAA17] hover:bg-[#E69500] text-[#1A1A1A] font-semibold rounded-full w-full"
              data-testid="mobile-cta-button"
            >
              Get Free Growth Audit
            </Button>
          </nav>
        </div>
      </div>
    </>
  );
};

export default Header;
